<script>
import { comandoDoTurno } from '@modules/aawz-intelligence-v2/utils/comando-na-fala'
import {
  ehCartaoDeOnboarding,
  idDaMensagemDaEtapaCorrente,
} from '@modules/aawz-intelligence-v2/ui-registry/chave-do-onboarding'
import { respostaAoCartaoDeOnboarding } from '@modules/aawz-intelligence-v2/utils/resposta-ao-cartao-de-onboarding'
import { runDaConversa } from '@modules/aawz-intelligence-v2/utils/run-da-conversa'
import ChatMessage from '@modules/aawz-intelligence-v2/components/chat-message/chat-message.vue'
import ChatIndiceMensagens from '@modules/aawz-intelligence-v2/components/chat-indice-mensagens/chat-indice-mensagens.vue'
import ChatIndiceArtefatos from '@modules/aawz-intelligence-v2/components/chat-indice-artefatos/chat-indice-artefatos.vue'
import PainelFlutuante from '@modules/aawz-intelligence-v2/components/painel-do-chat/painel-flutuante.vue'
import {
  extrairPerguntasAoGestor,
  extrairRespostasPareadas,
} from '@modules/aawz-intelligence-v2/utils/perguntas-ao-gestor'
import { getPersona } from '@modules/aawz-intelligence-v2/constants/Personas'
import {
  MINIMO_DE_MENSAGENS_PARA_INDICE,
  NOVAS_LINHAS,
  TRILHO_QUEBRADO,
} from '@modules/aawz-intelligence-v2/constants/TextChat'
import { mensagemAtivaPorScroll } from '@modules/aawz-intelligence-v2/utils/indice-mensagens/mensagem-ativa'
import { prefereMenosMovimento } from '@modules/aawz-intelligence-v2/utils/revelacao-de-texto'
import {
  encontrarPorDataAttr,
  rolarContainerAte,
  scrollTopParaAlinhar,
} from '@modules/aawz-intelligence-v2/utils/rolagem-do-trilho'
import larguraDoIndiceMixin from './messages-largura-do-indice-mixin'
import painelDoChatMixin from './messages-painel-do-chat-mixin'
import MessagesEntregasAoVivo from './messages-entregas-ao-vivo'

// Prefixo do `data-mensagem-id` da mensagem que ainda não tem id (a otimista,
// antes do `start` do stream). O índice do trilho precisa de um id ESTÁVEL por
// bolha para rolar até ela; `indice-N` é o mesmo desempate do `:key`.
const PREFIXO_ID_POR_INDICE = 'indice-'

// Índice de artefatos no modo INLINE: a classe de realce do cartão
// (`design/efeitos.sass`, carimbada no `.iv2-parte` da bolha) e quanto tempo
// ela fica — o bastante para o olho achar o cartão, curto para não poluir.
const CLASSE_ARTEFATO_EM_FOCO = 'iv2-parte--em-foco'
const DURACAO_DO_FOCO_MS = 1500

// Casca da mensagem em streaming: o conteúdo real chega por `streaming-*`. Fora do
// template para não recriar o objeto a cada render (identidade nova força o filho a
// re-renderizar) — os nomes em snake_case são os campos do backend.
const MENSAGEM_STREAMING = {
  role: 'assistant',
  id: null,
  content: '',
  thinking_steps: [],
  sources: [],
  feedback: null,
}

const DEFAULT_STORE_MODULE = 'aawz-intelligence-v2'

// Distância (px) do fim do sentinel até a borda inferior do container abaixo
// da qual ainda consideramos o usuário "grudado" no fim — ver updatePinnedToBottom.
const PINNED_TO_BOTTOM_THRESHOLD_PX = 80

// Ver perseguirFimAoAbrirConversa: intervalo entre conferências, teto de
// tempo pra desistir de perseguir o fim (nenhum import()/round-trip de
// cartão de tool deveria legitimamente passar disso) e nº de conferências
// seguidas sem crescer que consideramos "conteúdo estabilizou".
const PERSEGUIR_FIM_INTERVALO_MS = 120
const PERSEGUIR_FIM_LIMITE_MS = 2000
const PERSEGUIR_FIM_CONFERENCIAS_ESTAVEL = 3

export default {
  name: 'IntelligenceV2Messages',
  components: { ChatMessage, ChatIndiceMensagens, ChatIndiceArtefatos, PainelFlutuante, MessagesEntregasAoVivo },
  // Mede quanto o índice de artefatos pode ter de largura sem cobrir o trilho;
  // o cartão flutuante do painel da conversa (kill-switch `PAINEL_PROGRESSO_CONTEXTO_ATIVO`).
  mixins: [larguraDoIndiceMixin, painelDoChatMixin],
  props: {
    // spec-003 §5 (decisão §3-A opção (a)): namespace do store parametrizável.
    // Default preserva o IA CRM byte a byte; o chat do parceiro passa 'aiFramework'.
    //
    // ★ Ele também desce ao `ChatMessage` (spec-001 [T011]): é de lá que saem as
    // partes de tool do turno (`<ns>/toolPartsOrdenadas`). Sem repassar, o chat do
    // parceiro leria as partes do chat do CRM — o slice existe nos dois namespaces,
    // então o erro não daria exceção nenhuma: daria o cartão do turno errado.
    storeModule: {
      type: String,
      default: DEFAULT_STORE_MODULE,
    },
    // Só a rota cheia (layout fundido) limita a coluna de leitura. No popup e
    // na tela hospedeira a área já é estreita, e um teto ali sobraria margem
    // dos dois lados — é o que preserva o AC-8.3.
    larguraLimitada: {
      type: Boolean,
      default: false,
    },
    // Índice das mensagens do usuário na borda direita do trilho (traços +
    // popover, estilo ChatGPT). Opt-in: só a visão imersiva passa — o popup, o
    // painel do Hub e o `ai-framework` (que reusa este componente) não mudam.
    comIndiceDeMensagens: {
      type: Boolean,
      default: false,
    },
    // Índice "Artefatos desta conversa" (régua ou menu no canto superior
    // direito do trilho). Mesmo opt-in, pelo mesmo motivo. Com o modo artefato
    // ligado abre o aside; inline, rola até o cartão (`abrirArtefatoDoIndice`).
    comIndiceDeArtefatos: {
      type: Boolean,
      default: false,
    },
    // Atalhos e módulos do `/`, repassados à bolha do usuário (chip do comando).
    atalhos: {
      type: Array,
      default: function () {
        return []
      },
    },
    modulos: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      // ★ [19/09/2026] Exceção no render de QUALQUER descendente (uma bolha,
      // um cartão do registry). Enquanto não era tratada, o Vue abortava o
      // patch e a conversa sumia da tela — mas o composer continuava
      // aceitando turno, e cada turno chegava ao backend e era COBRADO sem
      // aparecer (13 envios, 12,36 créditos, no QA da spec-006).
      // Ver `errorCaptured` abaixo.
      erroNoTrilho: null,
      // A mensagem do usuário "atual" para o índice. É `data`, não computed:
      // depende de MEDIR o DOM, e é atualizada no mesmo rAF do `onScroll` e ao
      // mudar a lista (ver `atualizarMensagemAtiva`).
      idMensagemAtiva: '',
      // Só auto-rola pro fim quando o usuário já está perto dele — sem isso,
      // o watcher de responseTokensCount (10-60 updates/s durante o
      // streaming) puxa de volta pro fim a cada token, e rolar pra cima
      // manualmente pra ler algo vira impossível.
      isPinnedToBottom: true,
      // Throttle do onScroll via rAF — sem isso, cada evento de scroll
      // (dezenas por segundo num trackpad) roda 2 getBoundingClientRect()
      // imediatamente; agrupar num único rAF por frame limita a leitura a
      // no máximo 1x por frame de tela.
      scrollRafPending: false,
      // Handle do rAF pendente, pra poder cancelar em beforeDestroy — sem
      // isso, um scroll bem na hora de fechar o popup (chat-ia-pop-up.vue
      // remonta este componente via v-if) deixava o callback disparar depois
      // do componente já destruído, contra refs de um DOM já desmontado.
      scrollRafId: null,
      // Não vai em `data` reativo por acaso: é só um handle para desconectar.
      observadorDeConteudo: null,
      // ★ [owner, 21/09/2026] O respiro abaixo do ÚLTIMO turno, em px — ver
      // `medirEspacoDoTurno`. Zero fora de um turno iniciado nesta tela.
      alturaDoEspacoDoTurno: 0,
      // Só um turno INICIADO nesta tela tem respiro: a conversa reaberta
      // cresce (cartões lazy) sem que isso seja um turno.
      espacoDoTurnoAtivo: false,
      // Handle do rAF do observador — mesmo motivo do `scrollRafId`.
      crescimentoRafId: null,
      // Handle do loop de perseguirFimAoAbrirConversa — mesmo motivo
      // (setTimeout, não rAF, pra continuar funcionando com a aba oculta).
      perseguirFimTimeoutId: null,
      // Token da cadeia atual de perseguirFimAoAbrirConversa — um passo de
      // cadeia anterior que ainda dispare se reconhece pelo número e sai.
      perseguirFimGeracao: 0,
      // O prefixo do texto que a bolha em streaming chegou a mostrar — o
      // bastão que a bolha persistida retoma no `end` (ver `onRevelado`).
      textoReveladoDoTurno: '',
      textoNovasLinhas: NOVAS_LINHAS,
      // Handle do realce do cartão inline (`rolarAteArtefato`) — limpo no
      // `beforeDestroy` e substituído se outro clique vier antes de acabar.
      focoDoArtefatoTimeoutId: null,
    }
  },
  computed: {
    // Sem trilho, o `pa-4` do container é quem dá o respiro; com trilho, o
    // respiro é dele (ver `.messages__trilho`) e somar os dois empurraria a
    // coluna centralizada mais 16px de cada lado.
    classeSuperficie() {
      return this.larguraLimitada ? '' : 'pa-4'
    },
    classeTrilho() {
      return this.larguraLimitada ? 'ia-chat-trilho messages__trilho' : ''
    },
    // Constante do módulo não é alcançável do template — este computed é a
    // ponte, no mesmo molde dos demais textos do chat.
    TEXTO_ERRO() {
      return TRILHO_QUEBRADO
    },
    // Estes dois seguem `storeModule` como os demais. Enquanto liam o namespace fixo
    // 'aawz-intelligence-v2', o chat do ai-framework — que reusa este componente — recebia a
    // persona do IA CRM, cujo `permiteHtml: true` fazia a saída do agente ser renderizada como
    // HTML sob o sanitizador por regex. O default do prop preserva o IA CRM byte a byte.
    persona() {
      return getPersona(this.$store.getters[this.storeModule + '/persona'])
    },
    servicosCitadosStreaming() {
      return (
        this.$store.getters[this.storeModule + '/servicosCitadosStreaming'] ||
        []
      )
    },
    // A conversa aberta, para o cartão de serviço poder pedir o rascunho
    // PERSONALIZADO. Segue `storeModule` como os demais; namespace sem o getter
    // devolve `undefined` e o cartão degrada para o rascunho estrutural.
    idConversa() {
      const conversa =
        this.$store.getters[this.storeModule + '/selectedConversation']
      return (conversa && conversa.id) || ''
    },
    // Mesmo `||` defensivo do vizinho, pelo mesmo motivo: o `ai-framework` reusa
    // este componente com um `storeModule` que pode nao ter esta fatia.
    perguntasAoGestorStreaming() {
      return (
        this.$store.getters[this.storeModule + '/perguntasAoGestorStreaming'] ||
        []
      )
    },
    // Mesmo padrão, mesmo motivo. Aditivo: o `ai-framework` reusa este
    // componente com um `storeModule` que NÃO tem esta fatia — o getter volta
    // `undefined`, o `||` devolve `null` e a mensagem monta sem deliberação.
    // ★ Do #1961: a sala ao vivo. Enquanto a tool roda, o painel só existe como
    // eventos parciais (`painelParcialStreaming`, reduzido pelo getter); a
    // transcrição final chega no fim, pelo evento `painel`, e passa a mandar.
    // Sem este fallback o gestor não veria mesa nenhuma durante a deliberação —
    // que é justamente o que a sala ao vivo entrega.
    painelStreaming() {
      const final = this.$store.getters[this.storeModule + '/painelStreaming']
      if (final) return final
      return (
        this.$store.getters[this.storeModule + '/painelParcialStreaming'] ||
        null
      )
    },
    mensagemStreaming() {
      return MENSAGEM_STREAMING
    },
    messages() {
      return this.$store.getters[this.storeModule + '/messages'] || []
    },
    comandoDoTurnoAoVivo() {
      return comandoDoTurno(this.messages, this.messages.length, this.atalhos)
    },
    // As respostas do formulário viajam como uma mensagem `user` no formato
    // pareado — é o único canal que o backend tem. Mostrar esse texto como balão
    // expõe o formato de transporte ao gestor ("**pergunta**\nresposta"), que é
    // ruído: ele já sabe o que respondeu. Aqui a mensagem é RETIRADA da lista e
    // as respostas são devolvidas ao bloco que fez as perguntas, que passa a
    // exibi-las. Uma escrita, uma leitura — sem coluna nova no banco.
    /**
     * A mensagem que carrega a etapa CORRENTE da apresentação. Derivado do
     * thread (ver `idDaMensagemDaEtapaCorrente`), nunca de clique ou de estado
     * local — é o que faz o veredito sobreviver ao F5.
     */
    idDaEtapaCorrente() {
      return idDaMensagemDaEtapaCorrente(this.messages)
    },
    linhas() {
      const itens = []
      const ocultos = {}
      this.messages.forEach(function (msg, indice) {
        if (ocultos[indice]) return
        const perguntas = extrairPerguntasAoGestor(msg)
        let respostas = []
        if (perguntas.length) {
          const seguinte = this.messages[indice + 1]
          if (seguinte && seguinte.role === 'user') {
            respostas = extrairRespostasPareadas(seguinte.content, perguntas)
            if (respostas.length) ocultos[indice + 1] = true
          }
        }
        itens.push({
          msg: msg,
          indice: indice,
          respostas: respostas,
          // ★ [owner, 21/09/2026] Com que fala a pessoa respondeu ao cartão
          // de onboarding desta mensagem — derivado do thread, como
          // `etapaHistorica`, e pelo mesmo motivo: estado local do cartão
          // morre no F5 e não chega ao aside. Só para quem carrega o cartão;
          // as outras bolhas seguem com a moldura vazia.
          respostaDoCartao: this.respostaDoCartaoDaMensagem(msg),
          comando: msg.role === 'user' ? null : comandoDoTurno(this.messages, indice, this.atalhos),
          // O que a bolha carimba em `data-mensagem-id` — o índice do trilho
          // rola até ela por este valor.
          idDom: msg.id || PREFIXO_ID_POR_INDICE + indice,
        })
      }, this)
      // ★ [E-72] Qual bolha desenha os ARQUIVOS entregues de cada run.
      //
      // `id_run` é do TURNO: a mensagem do usuário e a do assistente
      // compartilham o mesmo. O cartão de entrega pertence à ÚLTIMA bolha de
      // ASSISTENTE do run — a do usuário não entrega nada, e um run pode
      // gravar duas mensagens de assistente (medido no banco local: 5 de
      // 3.725 runs), caso em que ancorar por `id_run` puro desenharia o
      // mesmo arquivo duas vezes.
      const ancoraPorRun = {}
      itens.forEach(function (item, i) {
        const idRun = item.msg && item.msg.id_run
        if (!idRun || item.msg.role !== 'assistant') return
        ancoraPorRun[idRun] = i
      })

      // `isLast` olha a lista JÁ sem os ocultos: com o balão escondido, quem
      // fecha a conversa é o bloco de perguntas, e é ele que segue editável
      // enquanto o gestor não respondeu.
      return itens.map(function (item, i) {
        const idRun = item.msg && item.msg.id_run
        return {
          ...item,
          isLast: i === itens.length - 1,
          ancoraDeArtefatos: Boolean(idRun) && ancoraPorRun[idRun] === i,
        }
      })
    },
    // O que o índice lista: só as bolhas do USUÁRIO que estão na tela — sai de
    // `linhas`, não de `messages`, para a resposta pareada oculta não virar uma
    // entrada que rola até um balão que não existe.
    mensagensDoUsuario() {
      return this.linhas
        .filter(function (linha) {
          return linha.msg.role === 'user'
        })
        .map(function (linha) {
          return { id: linha.idDom, texto: linha.msg.content || '' }
        })
    },
    mostraIndiceDeMensagens() {
      // Índice sobre conversa quebrada não tem função — e, mais grave, ele é
      // DESCENDENTE deste componente: mantê-lo renderizando é o caminho de
      // volta para a mesma exceção, quadro após quadro.
      if (this.erroNoTrilho) return false
      return (
        this.comIndiceDeMensagens &&
        this.mensagensDoUsuario.length >= MINIMO_DE_MENSAGENS_PARA_INDICE
      )
    },
    // O que as bolhas registraram (`chat-message.vue` →
    // `registrarArtefatoDaConversa`), já na ordem das mensagens. `|| []`
    // pelo mesmo motivo dos vizinhos: o `ai-framework` não tem a fatia.
    artefatosDaConversa() {
      return this.$store.getters[this.storeModule + '/artefatosDaConversa'] || []
    },
    // Só com a prop E com pelo menos um artefato: sem artefato o índice seria
    // chrome sem função. Não depende do kill-switch — inline o índice continua
    // como acesso rápido (rola até o cartão), e fora da imersiva a lista nem
    // é alimentada (`indiceDeArtefatosAtivo`). ★ E só DEPOIS da carga da
    // conversa, com a faixa ao lado do trilho já medida (14/09/2026): antes,
    // o índice montava no meio da carga com `larguraMaxima` nulo (= "cabe
    // menu"), nascia menu e caía para a régua quando a medida chegava — o
    // usuário via a troca de forma. Agora entra uma vez, já na visão certa,
    // pela transição da casca (template).
    mostraIndiceDeArtefatos() {
      // Mesma razão do índice de mensagens: sem trilho na tela, o índice das
      // entregas dele aponta para o que não está lá.
      if (this.erroNoTrilho) return false
      if (!this.comIndiceDeArtefatos) return false
      if (this.carregandoMensagens) return false
      if (this.larguraMaximaDoIndiceDeArtefatos === null) return false
      if (this.painelProgressoContextoAtivo) return this.mostraPainelFlutuante
      return this.artefatosDaConversa.length > 0
    },
    // A última do store não aparece na tela quando é resposta de formulário.
    ultimaMensagemOculta() {
      if (!this.messages.length) return false
      const ultimaDoStore = this.messages[this.messages.length - 1]
      const ultimaVisivel = this.linhas.length
        ? this.linhas[this.linhas.length - 1].msg
        : null
      return ultimaVisivel !== ultimaDoStore
    },
    chatLoading() {
      return this.$store.getters[this.storeModule + '/chatLoading']
    },
    responseTokens() {
      return this.$store.getters[this.storeModule + '/responseTokens'] || []
    },
    thinkingSteps() {
      return this.$store.getters[this.storeModule + '/thinkingSteps'] || []
    },
    toolCalls() {
      return this.$store.getters[this.storeModule + '/toolCalls'] || []
    },
    // ★ [task #169] O thread NÃO tem mais estado nem cartão de proposta: o
    // cartão é montado pelo REGISTRY, dentro da bolha
    // (`crm-confirmacao-card`, que lê o mapa de propostas por `id_proposta`).
    // Com os dois donos vivos a mesma proposta aparecia duas vezes, e só a
    // daqui sumia no reload — é o que a spec-010 [T018] documentou ao
    // removê-los.
    //
    // ⚠️ Os nomes dos computeds que saíram NÃO são citados aqui de propósito:
    // dois testes deste arquivo leem o FONTE (`fs.readFileSync`) e proíbem as
    // strings — um comentário que as mencione os derruba.
    // Namespace FIXO (US-13) — `{id_run, seq, estado, motivo}`.
    // ★ [21/09/2026] Recortado para a conversa DESTE trilho — com as abas, o
    // run de outra aba escondia a casca de streaming daqui
    // (`showStreamingMessage`) e trocava o "Pensando..." pelo "Aguardando sua
    // decisão" de um run que esta conversa nunca viu.
    runAtual() {
      const run = this.$store.getters['aawz-intelligence-v2/runAtual'] || null
      const conversa =
        this.$store.getters[this.storeModule + '/selectedConversation'] || null
      return runDaConversa(run, conversa && conversa.id)
    },
    // Troca "Pensando..." por "Aguardando sua decisão" (sem spinner) na
    // casca de streaming enquanto o run espera o card ser decidido.
    aguardandoDecisaoDoRun() {
      return Boolean(this.runAtual && this.runAtual.estado === 'awaiting_approval')
    },
    // ★ [review #2364] O estado do run DESTA conversa, para a moldura dos
    // cartões (`estado-do-run`, `cartao-do-chat.js::CHAVES_DA_MOLDURA`): o
    // `crm-confirmacao-card` o lia CRU do store fixo, e com as abas a proposta
    // expirada da aba B virava "Run cancelado" pelo run da aba A. Desce daqui,
    // já recortado, pelo `ChatMessage`. Vazio = sem run nesta conversa.
    estadoDoRunDaConversa() {
      return (this.runAtual && this.runAtual.estado) || ''
    },
    // [VERIFY-1/M-3] Plano do turno EM ANDAMENTO (US-10). Sem isso,
    // `chat-message.vue::plano` (ramo `isStreaming`) sempre recebia `null`, e
    // o acompanhamento ao vivo da lista de tarefas do agente (o ponto da
    // US-10) não existia na tela — só aparecia depois do `end`.
    //
    // ★ [20/09/2026] Lê o `storeModule`, não mais o namespace FIXO: o canal
    // `plano` passou a ser escrito na CONVERSA
    // (`apiChatModoAgenteEventos.js::_namespaceDoPlano`), que é quem o
    // cartão do painel já lia. Com o fixo dos dois lados, a bolha e o cartão
    // liam planos diferentes na Home IA — e era o cartão, o que alimenta o
    // relógio da encenação, que ficava sem nada. Fora da Home IA o
    // `storeModule` É o namespace fixo, então nada muda.
    planoStreaming() {
      return this.$store.getters[this.storeModule + '/plano'] || null
    },

    // [T101, spec-001 `pesquisa-internet-chat`] Os três canais transientes do
    // Modo Agente. Namespace FIXO pela MESMA razão do plano acima: só o módulo
    // principal tem Modo Agente, e `apiChatModoAgenteEventos` despacha nele
    // (uma superfície com namespace próprio — o Fee Fixo — roda outro módulo
    // Vuex, onde `buscaStreaming` não existe).
    //
    // ★ Antes desta task o caminho inteiro existia até aqui e PARAVA: o worker
    // emitia, o relay entregava, o store guardava, e nenhum `.vue` lia
    // (`api/chat-event-handlers.js:145-147` dizia isso em comentário).
    buscaStreaming() {
      return this.$store.getters['aawz-intelligence-v2/buscaStreaming'] || []
    },
    codigoStreaming() {
      return this.$store.getters['aawz-intelligence-v2/codigoStreaming'] || []
    },
    arquivoStreaming() {
      return this.$store.getters['aawz-intelligence-v2/arquivoStreaming'] || []
    },

    // Carga de uma conversa escolhida na sidebar. O getter existia no store
    // desde sempre e não era lido por ninguém — sem ele, entre o `setMessages([])`
    // e a resposta da API a lista ficava vazia e a tela caía no herói, que é o
    // "volta pra tela inicial e depois carrega".
    carregandoMensagens() {
      return this.$store.getters[this.storeModule + '/messagesLoading']
    },
    hasMessages() {
      return this.messages.length > 0
    },
    mostraTrilho() {
      // ⚠️ O erro DESLIGA o trilho de propósito, e não é só cosmético: quem
      // derrubou o render foi uma bolha desta mesma lista, então re-renderizar
      // com o erro na tela cairia na mesma exceção — laço de render. Uma
      // conversa parcialmente montada também não é confiável de ler.
      if (this.erroNoTrilho) return false
      return this.hasMessages || this.showStreamingMessage
    },
    // Explícito porque o trilho vive dentro de uma `<transition>` — um
    // `v-else` do slot não o alcançaria.
    mostraVazio() {
      // Conversa QUEBRADA não é conversa VAZIA: sem esta cláusula, o erro
      // convidaria o herói ("pergunte sobre seus clientes") para a tela e o
      // consultor concluiria que está tudo bem — que é exatamente o engano
      // que cobra.
      if (this.erroNoTrilho) return false
      return !this.mostraTrilho && !this.carregandoMensagens
    },
    // [VERIFY-3b/G-D17] Assim que o run entra em `cancelled`, a casca de
    // streaming ("Pensando...") some — sem isso, `chatLoading` continuava
    // `true` até o `end` fechar o turno (cancelarRun mantém o stream aberto
    // de propósito, T043-3), e o chip já 'cancelado' convivia com "Pensando..."
    // no mesmo frame (sinais contraditórios).
    showStreamingMessage() {
      if (this.runAtual && this.runAtual.estado === 'cancelled') return false
      return this.chatLoading
    },
    /**
     * ★ [owner, 21/09/2026] A resposta ainda está CHEGANDO à tela — o turno em
     * voo OU o fecho da revelação suave (a bolha persistida ainda escrevendo
     * o resto depois do `end`). É a janela em que subir para reler desgruda o
     * trilho e a pílula "↓ Novas linhas" aparece.
     */
    respostaAindaChegando() {
      return this.showStreamingMessage || Boolean(this.textoReveladoDoTurno)
    },
    mostraNovasLinhas() {
      return this.respostaAindaChegando && !this.isPinnedToBottom && !this.erroNoTrilho
    },
    messagesCount() {
      return this.messages.length
    },
    responseTokensCount() {
      return this.responseTokens.length
    },
  },
  watch: {
    /**
     * ★ [19/09/2026 · F-2] A conversa que quebrou é UMA, não a tela.
     *
     * `erroNoTrilho` não tinha reset, e o erro é do COMPONENTE, que NÃO
     * remonta na troca de conversa: o `<Messages>` do `view-chat.vue` não tem
     * `:key`. Medido: quebra em A, o consultor abre B — que renderiza
     * perfeitamente — e continua vendo “Não foi possível exibir esta
     * conversa” no lugar do trilho inteiro, sobre uma conversa que ele já
     * deixou. Única saída: recarregar a página.
     *
     * O sinal é a lista ESVAZIAR. `setMessages([])` é o que toda superfície
     * faz ao trocar de conversa e ao começar outra, e é o único ponto comum
     * às três que montam este componente — `ai-framework` e a aba de
     * comissões não têm `selectedConversation` para observar.
     *
     * ⚠️ Shallow de propósito (sem `deep`): compara a REFERÊNCIA que o
     * `setMessages` troca. Um deep watcher aqui seria o walker O(N²) que o
     * `messagesCount` abaixo existe para evitar.
     *
     * ⚠️ E NÃO desarme em qualquer re-render: voltar para a conversa ainda
     * quebrada tem de mostrar o erro de novo — e mostra, porque o render
     * estoura outra vez e o `errorCaptured` reacende. Desarmar sempre
     * reabriria o defeito original (tela muda que aceita pergunta e cobra).
     */
    messages(lista) {
      if (!lista || lista.length) return
      this.erroNoTrilho = null
    },
    // Shallow watch via count para evitar deep-walker O(N²) durante streaming.
    // responseTokens recebe 10-60 updates/s; ouvir apenas .length basta para scroll.
    messagesCount(newCount, oldCount) {
      this.$nextTick(() => {
        // Turno novo do usuário (não o assistant sendo persistido no fim do
        // streaming) — sobe a mensagem enviada pro topo em vez de perseguir
        // o fim, estilo Claude.ai/ChatGPT.
        const last = this.messages[this.messages.length - 1]
        // A resposta do formulário É uma mensagem `user`, mas ela é OCULTADA
        // (ver `linhas`). Subir ao topo miraria um balão que não existe na
        // tela e jogava a conversa lá para cima. Como o gesto foi um clique
        // deliberado em "Enviar respostas", seguimos a resposta que vem — daí
        // o pin forçado, em vez de respeitar o que o scroll dizia antes do
        // formulário colapsar (a medida ficou obsoleta no mesmo tick).
        if (this.ultimaMensagemOculta) {
          this.isPinnedToBottom = true
          this.scrollToBottom()
          return
        }
        // Lista foi de vazia pra cheia — troca de conversa em "Recentes" (ou
        // o 1º turno de uma conversa nova). Sempre ancora no fim, seja qual
        // for quem falou por último: não é decisão do CONTEÚDO reaberto, é o
        // ponto de partida de quem acabou de abrir a conversa. Sem isso, uma
        // conversa cujo último autor é a IA (o caso mais comum) caía no
        // fallback abaixo e só rolava se `isPinnedToBottom` tivesse sobrado
        // `true` por acaso da conversa anterior.
        if (oldCount === 0 && newCount > 0) {
          this.zerarEspacoDoTurno()
          this.isPinnedToBottom = true
          this.perseguirFimAoAbrirConversa()
          return
        }
        if (newCount > oldCount && last && last.role === 'user') {
          // requestAnimationFrame extra (além do $nextTick): dá uma volta a
          // mais pro browser fechar layout/paint antes de medir/rolar —
          // defensivo contra qualquer reflow tardio.
          requestAnimationFrame(() => this.scrollNewUserMessageToTop())
          return
        }
        this.scrollToBottom()
      })
    },
    // `v-if` recria o trilho (conversa nova, ou a primeira mensagem chegando):
    // o nó observado deixa de existir e o observer perderia o alvo.
    hasMessages() {
      if (!this.observadorDeConteudo) return
      this.observadorDeConteudo.disconnect()
      this.$nextTick(() => {
        if (this.$refs.trilho) this.observadorDeConteudo.observe(this.$refs.trilho)
      })
    },
    // Mensagem nova do usuário (ou conversa trocada): o índice ganha/perde
    // traços e a "atual" precisa ser remedida depois que o DOM assentou.
    mensagensDoUsuario() {
      if (!this.comIndiceDeMensagens) return
      this.$nextTick(() => this.atualizarMensagemAtiva())
    },
    responseTokensCount(newCount) {
      // responseTokens é resetado pra [] no mesmo instante em que uma nova
      // mensagem é enviada (limpa o buffer do turno anterior) — sem esse
      // guard, essa contagem zerando disparava scrollToBottom() no MESMO
      // tick do scrollNewUserMessageToTop() acima, desfazendo o scroll pro
      // topo antes de qualquer token real chegar. 0 nunca é conteúdo novo
      // pra seguir, então não há motivo pra rolar por causa dele.
      if (newCount === 0) return
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    // Turno novo: o bastão do anterior não pode sobrar (um turno que morreu
    // sem `end` nunca chega a `onRevelacaoConcluida`).
    chatLoading(ativo) {
      if (ativo) this.textoReveladoDoTurno = ''
    },
    // Entrando numa conversa: o container leva o `scrollTop` da conversa
    // anterior consigo, e a conversa nova nasceria com a viewport rolada.
    // Zera a rolagem do CONTAINER (por `scrollTop`, nunca `scrollIntoView`)
    // depois do `$nextTick`, quando o trilho antigo já saiu.
    carregandoMensagens(ativo) {
      if (!ativo) return
      this.zerarEspacoDoTurno()
      this.$nextTick(() => this.prepararContainerParaCarga())
    },
  },
  /**
   * ★ [19/09/2026] A conversa NÃO fica muda quando o render de uma bolha
   * estoura.
   *
   * O defeito medido no QA da spec-006: uma exceção não tratada fazia o Vue
   * abortar o patch, a tela ficava em branco — e o composer seguia aceitando
   * pergunta. Foram **13 turnos** que saíram, chegaram ao backend, gastaram
   * modelo (créditos 4.986,80 → 4.974,44) e nunca renderizaram. Tela vazia
   * que aceita input é a pior combinação: parece funcionando e cobra.
   *
   * Aqui é o ponto certo porque este componente é o pai de TODAS as bolhas, e
   * é montado pelas três superfícies (`view-chat`, `agent-chat` do
   * ai-framework, `view-comissoes-ia`) — o estado de erro vale para as três
   * sem cada uma repetir a guarda.
   *
   * ⚠️ **Sem `return false` de propósito.** Devolver `false` interromperia a
   * propagação, e o erro deixaria de chegar ao `Vue.config.errorHandler` que
   * o `plugins/observability.js` instala — perderíamos a telemetria do
   * defeito justo no caso em que ela mais importa. A UI é um ganho ADICIONAL,
   * não um substituto do report.
   *
   * Quem BLOQUEIA o envio é o pai, pelo evento `erro-no-trilho`: este
   * componente não é dono do composer (ver `view-chat.vue`).
   */
  errorCaptured(err, vm, info) {
    this.erroNoTrilho = {
      mensagem: (err && err.message) || String(err || ''),
      // `info` do Vue: "render", "created hook", etc. Ajuda a distinguir
      // bolha quebrada de cartão do registry quebrado no report do usuário.
      onde: info || '',
      componente: (vm && vm.$options && vm.$options.name) || '',
    }
    this.$emit('erro-no-trilho', this.erroNoTrilho)
  },
  mounted() {
    const el = this.$refs.scrollContainer
    if (el) el.addEventListener('scroll', this.onScroll, { passive: true })
    // O respiro do turno é medido contra a JANELA do container: redimensionar
    // a janela muda a conta sem o trilho crescer (o `ResizeObserver` só olha o
    // trilho). Mesmo rAF do observador, para não medir por evento.
    window.addEventListener('resize', this.aoRedimensionarJanela, { passive: true })
    this.observarCrescimentoDoConteudo()
    // chat-ia-pop-up.vue usa v-if pra abrir/fechar o popup — cada abertura
    // remonta este componente do zero, e o scroll nasce em scrollTop:0 (topo).
    // Numa conversa já longa, reabrir mostraria o TOPO em vez da última
    // mensagem; força o scroll pro fim assim que o conteúdo estiver montado.
    this.$nextTick(() => requestAnimationFrame(() => this.scrollToBottom()))
  },
  beforeDestroy() {
    const el = this.$refs.scrollContainer
    if (el) el.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.aoRedimensionarJanela)
    if (this.scrollRafId !== null) cancelAnimationFrame(this.scrollRafId)
    if (this.crescimentoRafId !== null) cancelAnimationFrame(this.crescimentoRafId)
    if (this.perseguirFimTimeoutId !== null) clearTimeout(this.perseguirFimTimeoutId)
    if (this.focoDoArtefatoTimeoutId !== null) clearTimeout(this.focoDoArtefatoTimeoutId)
    if (this.observadorDeConteudo) {
      this.observadorDeConteudo.disconnect()
      this.observadorDeConteudo = null
    }
  },
  methods: {
    // Os dois watchers de scroll só conhecem MENSAGEM NOVA e TOKEN DE TEXTO.
    // Nada disso dispara quando um BLOCO cresce — a deliberação do painel
    // ganhando áreas, o raciocínio abrindo, o formulário de perguntas
    // aparecendo, os serviços citados. O conteúdo empurrava a conversa para
    // baixo e a visão ficava para trás, exigindo rolagem manual: na prática o
    // acompanhamento automático só funcionava com texto puro.
    //
    // O `ResizeObserver` olha o TRILHO (o wrapper que contém todas as
    // mensagens), então qualquer crescimento lá dentro chega aqui, sem este
    // componente precisar conhecer cada bloco. O `isPinnedToBottom` continua
    // mandando: quem rolou para cima de propósito não é puxado de volta.
    /**
     * A apresentação avançou dentro de uma bolha que JÁ estava na tela.
     *
     * ★ **Força o pin antes de rolar, e é isso que o distingue do observador.**
     * O `ResizeObserver` abaixo já reage ao crescimento do trilho, mas respeita
     * `isPinnedToBottom` — e quem acabou de ler a boas-vindas (cartão alto) não
     * está no fim, então a etapa nasceria fora da tela. Aqui o gesto foi um
     * clique DELIBERADO em "Continuar": a pessoa pediu o próximo passo, e
     * seguir o que ela pediu é o comportamento certo. Mesmo raciocínio do pin
     * forçado de `ultimaMensagemOculta` (o "Enviar respostas").
     *
     * O `$nextTick` + `requestAnimationFrame` espera o cartão entrar no DOM e o
     * browser fechar layout — sem isso a medida sai com a altura de antes.
     */
    /**
     * Esta bolha carrega uma etapa que já é HISTÓRICO?
     *
     * ★ A pergunta é a NEGATIVA de propósito: a prop que ela alimenta nasce
     * `false` (`vue/no-boolean-default`), e "não é histórico" tem de ser o
     * default — é o comportamento de sempre, com o cartão aberto.
     *
     * ⚠️ Sem etapa nenhuma no thread, NADA é histórico: `idDaEtapaCorrente`
     * `null` devolve `false` para todo mundo, em vez de marcar a conversa
     * inteira como passado.
     *
     * ⚠️ E a bolha otimista ainda não tem `id`: sem o guard, ela nunca seria a
     * corrente e nasceria recolhida no instante em que a etapa chega.
     */
    ehEtapaHistorica(mensagem) {
      if (!this.idDaEtapaCorrente) return false
      const id = mensagem && mensagem.id
      if (!id) return false
      return id !== this.idDaEtapaCorrente
    },
    /**
     * A fala com que a pessoa respondeu ao cartão de onboarding da mensagem —
     * `''` para toda bolha que não o carrega. O util já confere a origem; o
     * `some` aqui é só para o thread inteiro não pagar a varredura por bolha.
     */
    respostaDoCartaoDaMensagem(mensagem) {
      const chamadas = (mensagem && mensagem.tool_calls) || []
      if (!chamadas.some(ehCartaoDeOnboarding)) return ''
      return respostaAoCartaoDeOnboarding(this.messages, mensagem.id)
    },
    onAvancouApresentacao() {
      this.isPinnedToBottom = true
      // ⚠️ **Um `scrollToBottom()` só NÃO resolve, e foi o que o owner mediu**
      //    ("nem o continuar do primeiro card está funcionando"): o cartão da
      //    etapa entra pelo `transition-group` (`iv2-parte`, `appear`), então
      //    no tick seguinte ao clique ele ainda está em `opacity: 0` com
      //    `translateY(10px)` e altura quase zero — rolar ali mira um conteúdo
      //    que ainda não cresceu, e os ~220ms de animação acontecem DEPOIS,
      //    deixando a etapa fora da tela. Somam-se a isso o colapso do cartão
      //    anterior (encolhe) e o `v-expand-transition` da casca.
      //
      //    `perseguirFimAoAbrirConversa` existe exatamente para conteúdo que
      //    cresce em incrementos: confere a cada 120ms, desiste quando
      //    estabiliza e tem teto de 2s. É o mesmo mecanismo usado ao abrir uma
      //    conversa cujo fim tem cartão de tool com import lazy.
      this.$nextTick(() => this.perseguirFimAoAbrirConversa())
    },
    observarCrescimentoDoConteudo() {
      if (typeof ResizeObserver === 'undefined') return
      // rAF, e não chamada direta: `scrollToBottom` altera o scroll, o que muda
      // o layout, o que reentra no observador — é a receita do warning
      // `ResizeObserver loop completed with undelivered notifications`, que
      // neste repo REPROVA teste (o `setup.js` faz console.error lançar). Mesmo
      // remédio que o `onScroll` daqui já aplica.
      this.observadorDeConteudo = new ResizeObserver(() => {
        if (this.crescimentoRafId) return
        this.crescimentoRafId = requestAnimationFrame(() => {
          this.crescimentoRafId = null
          // A resposta que cresce COME o respiro na mesma medida: o
          // scrollHeight fica parado até o respiro zerar, e só então o
          // trilho passa a seguir o fim (`scrollToBottom`).
          this.medirEspacoDoTurno()
          this.scrollToBottom()
        })
      })
      this.$nextTick(() => {
        // O trilho só existe depois da primeira mensagem, e é recriado pelo
        // `v-if` quando a conversa esvazia — daí reobservar a cada troca.
        if (this.$refs.trilho) this.observadorDeConteudo.observe(this.$refs.trilho)
      })
    },
    prepararContainerParaCarga() {
      if (this.mostraTrilho) return
      const el = this.$refs.scrollContainer
      if (el) el.scrollTop = 0
    },
    // Mede contra o sentinel (fim do conteúdo real), não `el.scrollHeight` —
    // este último some com qualquer padding-bottom que o container ganhe no
    // futuro, então medir contra o sentinel evita reintroduzir esse
    // acoplamento frágil (já causou bug antes — ver histórico no estilo
    // deste componente).
    onScroll() {
      if (this.scrollRafPending) return
      this.scrollRafPending = true
      this.scrollRafId = requestAnimationFrame(() => {
        this.scrollRafPending = false
        this.scrollRafId = null
        this.updatePinnedToBottom()
        // Mesmo rAF, mesma leitura de layout: o índice acompanha a rolagem
        // sem um segundo throttle.
        if (this.comIndiceDeMensagens) this.atualizarMensagemAtiva()
      })
    },
    // Quem decide a "atual" é `mensagemAtivaPorScroll` (regra pura, testada
    // com a geometria medida ao vivo); aqui só se mede o DOM. Tudo em
    // coordenadas do CONTEÚDO (`viewport − topo do container + scrollTop`),
    // por `getBoundingClientRect` e não `offsetTop` — rects não dependem de
    // quem é o `offsetParent` da bolha.
    //
    // O "fim" é o SENTINEL, não `el.scrollHeight`: o spacer de 60vh entra no
    // `scrollHeight` durante o streaming, e com ele o scroll nunca estaria "no
    // fim" enquanto a resposta chega. Tolerância = a mesma folga de "grudado
    // no fim" do `updatePinnedToBottom` — é um conceito só: quem está
    // grudado no fim está lendo a última pergunta.
    atualizarMensagemAtiva() {
      const el = this.$refs.scrollContainer
      if (!el) return
      const bolhas = el.querySelectorAll('.chat-message--user[data-mensagem-id]')
      if (!bolhas.length) {
        this.idMensagemAtiva = ''
        return
      }
      const areaVisivel = el.getBoundingClientRect()
      const paraOConteudo = function (yNaViewport) {
        return yNaViewport - areaVisivel.top + el.scrollTop
      }
      const sentinel = this.$refs.scrollSentinel
      const fimDoConteudo = sentinel
        ? paraOConteudo(sentinel.getBoundingClientRect().bottom)
        : el.scrollHeight
      const ativa = mensagemAtivaPorScroll({
        scrollTop: el.scrollTop,
        clientHeight: areaVisivel.height,
        scrollHeight: fimDoConteudo,
        offsets: Array.prototype.map.call(bolhas, function (bolha) {
          return paraOConteudo(bolha.getBoundingClientRect().top)
        }),
        tolerancia: PINNED_TO_BOTTOM_THRESHOLD_PX,
      })
      this.idMensagemAtiva = bolhas[ativa].getAttribute('data-mensagem-id')
    },
    // As duas rolagens por índice (mensagem e artefato inline) são a MESMA
    // conta e o mesmo `scrollTo` no CONTAINER (`utils/rolagem-do-trilho.js`;
    // nunca `scrollIntoView` — ver `scrollNewUserMessageToTop`), e as duas
    // respeitam `prefers-reduced-motion`: uma regra de movimento, não duas.
    //
    // ⚠️ A rolagem dispara `@scroll` e pode desgrudar `isPinnedToBottom` — e é
    // o correto: quem foi até uma pergunta antiga SAIU do fim. O guard de
    // `updatePinnedToBottom` não é afetado (não passa por `carregandoMensagens`
    // nem por `perseguirFimTimeoutId`).
    rolarContainerAteOAlvo(alvo) {
      const el = this.$refs.scrollContainer
      rolarContainerAte(el, scrollTopParaAlinhar(el, alvo), { suave: !prefereMenosMovimento() })
    },
    // Rola até a bolha com aquele `data-mensagem-id`.
    rolarAteMensagem(id) {
      const alvo = encontrarPorDataAttr(this.$refs.scrollContainer, 'mensagem-id', id)
      if (!alvo) return
      // Aceso na hora: a rolagem suave leva centenas de ms, e o traço só
      // trocaria no fim dela.
      this.idMensagemAtiva = String(id)
      this.rolarContainerAteOAlvo(alvo)
    },
    // Índice de artefatos: a linha abre o aside SEMPRE que há `parteReal` —
    // prévia OU cartão inline (o aside é só outra forma de ver o mesmo
    // cartão; decisão do usuário, 14/09/2026: "tudo no aside"). São os
    // MESMOS despachos do clique na prévia do thread
    // (`chat-message.vue::onAbrirArtefato`/`onFecharArtefato`) — quem decide
    // ativo × não ativo é o card mini, pelo `artefatoIdentidadeAtiva` —, SEM
    // rolar até a bolha (decisão do usuário, 11/09/2026).
    //
    // Sem `parteReal` não há o que abrir, e o índice rola até o cartão,
    // realçando-o. O mixin só registra `parteReal: null` em dois casos
    // (`renderiza-partes.js::abreNoAside`): cartão de ESCRITA (o aside SOBE o
    // `@respond` desde 17/09/2026, mas monta o cartão sem os atributos de
    // moldura — `state`/`tool-call-id`/`turno-em-voo` —, então a confirmação
    // continua inline) e painel indisponível (kill-switch desligado — não há
    // aside). É por eles que `rolarAteArtefato`/`realcarArtefato` ficam.
    abrirArtefatoDoIndice({ identidade, parteReal } = {}) {
      if (parteReal) {
        this.$store.dispatch(this.storeModule + '/abrirArtefato', parteReal)
        return
      }
      this.rolarAteArtefato(identidade)
    },
    fecharArtefatoDoIndice() {
      this.$store.dispatch(this.storeModule + '/fecharArtefato')
    },
    // Rola até o cartão inline (`data-artefato-id`, carimbado no invólucro
    // `.iv2-parte` da bolha) e o realça por `DURACAO_DO_FOCO_MS`.
    rolarAteArtefato(identidade) {
      const alvo = encontrarPorDataAttr(this.$refs.scrollContainer, 'artefato-id', identidade)
      if (!alvo) return
      this.rolarContainerAteOAlvo(alvo)
      this.realcarArtefato(alvo)
    },
    // Um foco por vez: o clique seguinte tira o anel do cartão anterior antes
    // de acender o novo, e o timer antigo não apaga o novo no meio.
    // A classe é imperativa: se o Vue reescrever o `class` desse invólucro
    // antes dos 1,5s (flip de `validando` ou `iv2-parte-move`), o anel some
    // mais cedo — falha benigna, o realce é só orientação.
    realcarArtefato(alvo) {
      if (this.focoDoArtefatoTimeoutId !== null) clearTimeout(this.focoDoArtefatoTimeoutId)
      const el = this.$refs.scrollContainer
      const anterior = el && el.querySelector('.' + CLASSE_ARTEFATO_EM_FOCO)
      if (anterior && anterior !== alvo) anterior.classList.remove(CLASSE_ARTEFATO_EM_FOCO)
      alvo.classList.add(CLASSE_ARTEFATO_EM_FOCO)
      this.focoDoArtefatoTimeoutId = setTimeout(() => {
        alvo.classList.remove(CLASSE_ARTEFATO_EM_FOCO)
        this.focoDoArtefatoTimeoutId = null
      }, DURACAO_DO_FOCO_MS)
    },
    updatePinnedToBottom() {
      // Enquanto a conversa está carregando (o spinner colapsa o trilho pra um
      // `min-height: 200px`) ou enquanto perseguirFimAoAbrirConversa() está
      // perseguindo o fim, o CONTEÚDO pode colapsar/crescer de tamanho — e o
      // BROWSER clampa o scrollTop sozinho quando isso acontece, disparando um
      // evento `scroll` nativo que não é gesto do usuário. Sem este guard, esse
      // scroll espúrio podia corromper isPinnedToBottom pro resto da conversa:
      // tanto scrollToBottom() quanto o ResizeObserver permanente
      // (observarCrescimentoDoConteudo) dependem dela igualmente, e uma leitura
      // errada aqui os transformava em no-op silencioso até a próxima troca de
      // conversa — os dois sintomas relatados (scroll parado no meio ao reabrir
      // conversa, e scroll que não acompanha um cartão assíncrono terminando de
      // montar) vinham dessa mesma corrupção.
      if (this.carregandoMensagens || this.perseguirFimTimeoutId !== null) return
      const el = this.$refs.scrollContainer
      const sentinel = this.$refs.scrollSentinel
      if (!el || !sentinel) return
      const distanceFromBottom =
        sentinel.getBoundingClientRect().bottom -
        el.getBoundingClientRect().bottom
      this.isPinnedToBottom = distanceFromBottom < PINNED_TO_BOTTOM_THRESHOLD_PX
    },
    // Atribuição direta de scrollTop (não `scrollIntoView`) — chamado a cada
    // leva de tokens (até 60x/s) durante o streaming, junto de uma
    // re-renderização completa do markdown; `scrollIntoView` é uma API bem
    // mais cara (resolve alinhamento subindo a cadeia inteira de ancestrais
    // roláveis) e, nos dois juntos a cada frame, isso é o que deixava a
    // digitação com menos fluidez que a do especialista Fee Fixo (que usa
    // `scrollTop = scrollHeight` — ver fee-fixo-ia-especialista.vue).
    //
    // Não usamos `el.scrollHeight` puro (como o Fee Fixo) porque o spacer
    // (`.messages__bottom-spacer`, ver estilo abaixo) entra nesse cálculo —
    // rolaria pro fim do espaço vazio, não pro fim do conteúdo real. Calcular
    // a posição do sentinel via `getBoundingClientRect` é o mesmo tipo de
    // leitura que `scrollIntoView` faria por baixo, só sem o algoritmo de
    // alinhamento entre containers — e nunca rola pra trás (mesma correção
    // que o `block:'nearest'` garantia, ver histórico logo abaixo).
    scrollToBottom() {
      if (!this.isPinnedToBottom) return
      const el = this.$refs.scrollContainer
      const sentinel = this.$refs.scrollSentinel
      if (!el || !sentinel) return
      const elRect = el.getBoundingClientRect()
      const sentinelRect = sentinel.getBoundingClientRect()
      // Fim do sentinel já visível dentro do container — nada a fazer
      // (replica a semântica "nearest": só rola quando precisa).
      if (sentinelRect.bottom <= elRect.bottom) return
      el.scrollTop += sentinelRect.bottom - elRect.bottom
    },
    // Abrir uma conversa cujo fim renderiza cartão de tool (ação de front,
    // tabela cujo import do componente é lazy, card que ainda faz o próprio
    // fetch) não cresce de uma vez só — medido ao vivo, o trilho sobe em
    // vários incrementos pequenos ao longo de uns 300-600ms. O
    // ResizeObserver existe pra perseguir justamente isso, mas o callback
    // dele é embrulhado em `requestAnimationFrame` (ver
    // observarCrescimentoDoConteudo) — e rAF **não dispara** enquanto a aba
    // está oculta/sem foco (o Chrome não só atrasa, ele pausa de vez; medido
    // ao vivo: um rAF agendado ficou pendente por mais de 10s numa aba
    // oculta). `setTimeout`, ao contrário, continua disparando mesmo aí.
    // Em vez de um único tiro (ou de depender só do observer), persegue o
    // fim por uma janela curta: reconfere a cada intervalo curto e só
    // desiste quando o trilho parar de crescer por algumas conferências
    // seguidas (ou o teto de tempo estourar — não deveria disparar em uso
    // normal, é só a rede de segurança).
    perseguirFimAoAbrirConversa() {
      // Duas conversas abertas em sequência rápida: a cadeia da primeira
      // continuaria rolando por cima da segunda. Cancela a pendente e
      // invalida o passo dela pelo token.
      if (this.perseguirFimTimeoutId !== null) clearTimeout(this.perseguirFimTimeoutId)
      this.perseguirFimGeracao += 1
      const geracao = this.perseguirFimGeracao
      // Correção imediata, síncrona: o primeiro chute já acerta na imensa
      // maioria dos casos (conteúdo que não depende de nada assíncrono), e
      // o usuário não deveria esperar nem um tick pra ver o fim nesse caso.
      this.scrollToBottom()
      const inicio = performance.now()
      let ultimaAltura = -1
      let conferenciasEstavel = 0
      const passo = () => {
        if (geracao !== this.perseguirFimGeracao) return
        this.perseguirFimTimeoutId = null
        this.scrollToBottom()
        const trilho = this.$refs.trilho
        const altura = trilho ? trilho.getBoundingClientRect().height : 0
        if (altura === ultimaAltura) {
          conferenciasEstavel += 1
        } else {
          conferenciasEstavel = 0
          ultimaAltura = altura
        }
        const esgotou = performance.now() - inicio > PERSEGUIR_FIM_LIMITE_MS
        if (conferenciasEstavel >= PERSEGUIR_FIM_CONFERENCIAS_ESTAVEL || esgotou) return
        this.perseguirFimTimeoutId = setTimeout(passo, PERSEGUIR_FIM_INTERVALO_MS)
      }
      this.perseguirFimTimeoutId = setTimeout(passo, PERSEGUIR_FIM_INTERVALO_MS)
    },
    // Ancora a última mensagem (a que o usuário acabou de mandar) perto do
    // topo da área visível, rolando o quanto o conteúdo real permitir (pode
    // não chegar no topo exato em conversas muito curtas, sem conteúdo
    // suficiente abaixo pra "empurrar" a rolagem — aceitável, ver histórico
    // no estilo deste componente sobre a tentativa anterior com padding
    // artificial). Reengata isPinnedToBottom pro streaming da resposta, que
    // vem a seguir, voltar a auto-rolar normalmente assim que crescer além da
    // área visível.
    //
    // ★ Rola o CONTAINER por `scrollTop`, NUNCA `scrollIntoView` (12/09/2026).
    // `scrollIntoView` alinha o alvo subindo a cadeia INTEIRA de ancestrais
    // roláveis — e `overflow: hidden` conta como rolável por programação.
    // Medido em `/home-ia` (viewport 962): com a conversa curta demais para
    // este container rolar até o topo da bolha, ele seguia para o
    // `.intelligence-v2-chat__area` (`overflow: hidden`) e o rolava 60px —
    // o composer e o disclaimer subiam 60px e sobrava uma faixa vazia no
    // rodapé, até o próximo `v-if` do trilho clampar o scroll de volta. O
    // cálculo por rects é o mesmo de `rolarAteMensagem`, que já rola por
    // índice em produção.
    scrollNewUserMessageToTop() {
      const el = this.$refs.scrollContainer
      if (!el) return
      // `.chat-message--user`, não `.chat-message` genérico — este último
      // também bate na mensagem do assistente em streaming (ícone de marca
      // animado, sem texto de placeholder) se ela já tiver entrado no DOM
      // nesse mesmo tick, e aí o alvo vira a mensagem errada.
      const rows = el.querySelectorAll('.chat-message--user')
      const lastRow = rows[rows.length - 1]
      if (!lastRow) return
      // O respiro é medido e escrito no DOM AGORA (não só no `data`): sem a
      // folga já no scrollHeight, a bolha nova não tem para onde subir.
      this.espacoDoTurnoAtivo = true
      this.medirEspacoDoTurno()
      el.scrollTop = scrollTopParaAlinhar(el, lastRow)
      this.isPinnedToBottom = true
    },
    /**
     * ★ [owner, 21/09/2026] O respiro abaixo do ÚLTIMO turno é MEDIDO, e nunca
     * colapsa no `end`.
     *
     * Era um bloco de 60vh ligado a `showStreamingMessage`: subia a pergunta
     * ao topo, mas sumia de uma vez no `end` — o scrollHeight encolhia, o
     * browser clampava o scrollTop e a tela "voltava para baixo" no exato
     * instante em que a resposta ficava pronta (print do owner: pergunta no
     * topo durante o "Pensando...", conversa inteira rolada ao fim depois).
     * Estilo Claude/ChatGPT: o último turno ocupa a janela; a resposta come o
     * respiro conforme cresce; pronta, fica onde está.
     *
     * A medida é `janela − (topo da última bolha do usuário → sentinel)`,
     * nunca negativa. Escrita também DIRETO no elemento porque quem chama em
     * seguida rola pelo `scrollTop` no mesmo tick, antes do re-render.
     * Zerado ao esvaziar/abrir conversa (`zerarEspacoDoTurno`): conversa
     * reaberta não nasce com respiro.
     */
    medirEspacoDoTurno() {
      if (!this.espacoDoTurnoAtivo) return
      const el = this.$refs.scrollContainer
      const sentinel = this.$refs.scrollSentinel
      if (!el || !sentinel) return
      const rows = el.querySelectorAll('.chat-message--user')
      const lastRow = rows[rows.length - 1]
      if (!lastRow) return this.zerarEspacoDoTurno()
      const alturaDoTurno = sentinel.getBoundingClientRect().bottom - lastRow.getBoundingClientRect().top
      this.aplicarEspacoDoTurno(Math.max(0, Math.round(el.clientHeight - alturaDoTurno)))
    },
    zerarEspacoDoTurno() {
      this.espacoDoTurnoAtivo = false
      this.aplicarEspacoDoTurno(0)
    },
    aoRedimensionarJanela() {
      if (!this.espacoDoTurnoAtivo || this.crescimentoRafId) return
      this.crescimentoRafId = requestAnimationFrame(() => {
        this.crescimentoRafId = null
        this.medirEspacoDoTurno()
        this.scrollToBottom()
      })
    },
    aplicarEspacoDoTurno(altura) {
      this.alturaDoEspacoDoTurno = altura
      const espaco = this.$refs.espacoDoTurno
      if (espaco) espaco.style.height = altura + 'px'
    },
    onFeedback(payload) {
      this.$emit('feedback', payload)
    },
    // Vue 2 não propaga evento de neto para avô: sem este repasse, o `open-service`
    // emitido por `chat-message` nunca chega à casca (lacuna L-06).
    onOpenService(payload) {
      this.$emit('open-service', payload)
    },
    // ★ [spec-010 T018] O canal genérico dos cartões (`respond`) sobe daqui
    // para quem monta o thread — é por ele que o "Confirmar" da proposta de
    // escrita chega à superfície, que fala com o motor de chat. Vue 2 não
    // propaga evento de neto para avô: sem este repasse, o clique morre na
    // bolha (a mesma lacuna L-06 do `open-service`).
    //
    // ★ O card de confirmação embutido aqui (com o preview pendente do store, o
    // gate que o mostrava e os eventos de confirmar/cancelar) SAIU no merge de
    // 27/08/2026 — os nomes não estão escritos porque o teste desta suíte
    // procura por eles no FONTE, e citá-los num comentário o reprovaria.
    // A proposta de escrita passou a ser um CARTÃO do turno —
    // `crm/confirmacao`, dono no thread pelo `UI_REGISTRY`, decidido pelo
    // `state` que o backend emite (`partes.js:31-39` explica por que a chave
    // deixou de ser gate legado). Com isso o cartão sobrevive ao fim do turno e
    // ao reload, o que o estado antigo do store nunca fez — e manter os dois
    // desenharia a mesma proposta duas vezes.
    onRespond(evento) {
      this.$emit('respond', evento)
    },
    // ★ [task #169] Os quatro repasses da US-8 (`decidir`,
    // `pedir-nova-proposta`, `tentar-novamente`, `expirou-por-relogio`)
    // saíram: o cartão agora sobe as quatro pelo canal `respond` que já
    // atravessa esta cadeia, e `view-chat.vue::responderCartao` as roteia.
    // Mesmo repasse de neto para avô: o `ask` sobe do cartão de perguntas até
    // quem monta o thread, que o transforma no próximo turno do gestor.
    onAsk(texto) {
      this.$emit('ask', texto)
    },
    // Mesmo repasse de neto para avô do `open-service` acima.
    onResponderPerguntas(texto) {
      this.$emit('responder-perguntas', texto)
    },
    // ── A passagem de bastão da revelação suave (11/09/2026) ────────────────
    //
    // A bolha em streaming mostra o texto a uma taxa constante e pode estar
    // atrás do recebido quando o `end` chega — e o `end` a DESTRÓI (este
    // componente monta a persistida no mesmo tick). Sem o bastão, a
    // persistida entraria com o texto inteiro: um salto no fecho. A bolha em
    // streaming emite o prefixo a cada avanço; guardamos o último e o
    // entregamos à bolha seguinte (`textoReveladoInicial`), que retoma dali e
    // conclui em ≤300ms (`chat-message.revelacao.js`).
    //
    // ★ [owner, 21/09/2026] A bolha PERSISTIDA também emite `revelado` enquanto
    // completa (o fecho, ou o bloco do Supervisor revelado do zero): é o que
    // mantém `respostaAindaChegando` verdadeiro até a última palavra — a
    // pílula "↓ Novas linhas" vale para o texto que ainda está entrando, não
    // só para o turno em voo. A prop `textoReveladoInicial` só é lida no
    // `created()` da bolha, então o próprio prefixo voltando por ela é inócuo.
    onRevelado(texto) {
      this.textoReveladoDoTurno = texto
    },
    onRevelacaoConcluida() {
      this.textoReveladoDoTurno = ''
    },
    /**
     * ★ [owner, 21/09/2026] A pílula "↓ Novas linhas": regruda o trilho ao fim
     * e rola até lá. É o MESMO `scrollToBottom` que o streaming usa — com o
     * `isPinnedToBottom` religado antes, senão ele sai na primeira linha.
     */
    voltarAoFimDaResposta() {
      this.isPinnedToBottom = true
      this.scrollToBottom()
    },
  },
}
</script>

<template>
  <div
    ref="scrollContainer"
    class="messages flex-grow-1"
    :class="classeSuperficie"
    style="overflow-y: auto"
  >
    <!-- Índice das mensagens do usuário, na borda direita e centrado na ÁREA
         VISÍVEL. Este `.messages` é o próprio container que rola, então um
         `position: absolute` aqui dentro rolaria junto com o conteúdo — a casca
         é `position: sticky; top: 50%` com altura zero (não entra no
         `ResizeObserver` do trilho nem no `gap` dele, porque fica FORA do
         trilho) e a coluna é absoluta dentro dela. Primeiro filho de propósito:
         o sticky só consegue "descer" até onde o bloco que o contém permite. -->
    <!-- Índice de artefatos, no canto superior direito da ÁREA VISÍVEL —
         mesmo truque do índice de mensagens abaixo: casca `sticky; top: 0`
         de altura zero (fora do trilho, fora do ResizeObserver), índice
         absoluto dentro dela. Acima do índice de mensagens no z-index.
         `largura-maxima` é a faixa livre entre o trilho centralizado e a
         borda do container (mixin `messages-largura-do-indice-mixin`): o
         menu se limita a ela ou vira a régua — nunca cobre a bolha. -->
    <!-- Entra como o trilho entra (`iv2-carga-trilho`: fade + 6px, só na
         entrada; `appear` porque a casca nasce junto com a conversa já
         carregada) — e SÓ depois da carga e da medida da faixa, já na visão
         certa (ver `mostraIndiceDeArtefatos`). A saída é seca de propósito:
         ao trocar de conversa a lista esvazia e o índice anterior não pode
         ficar em fade por cima da barra de carga. -->
    <transition name="iv2-carga-trilho" appear>
      <div v-if="mostraIndiceDeArtefatos" class="messages__indice-artefatos">
        <!-- Painel da conversa ligado: o cartão flutuante substitui índice e
             régua; as entregas abrem no aside pelos mesmos handlers. -->
        <PainelFlutuante
          v-if="painelProgressoContextoAtivo"
          class="messages__indice-artefatos-chip"
          :style="estiloDoPainelFlutuante"
          :store-module="storeModule"
          :largura-maxima="larguraMaximaDoIndiceDeArtefatos"
          @abrir="abrirArtefatoDoIndice"
          @fechar="fecharArtefatoDoIndice"
        />
        <ChatIndiceArtefatos
          v-else
          class="messages__indice-artefatos-chip"
          :artefatos="artefatosDaConversa"
          :store-module="storeModule"
          :largura-maxima="larguraMaximaDoIndiceDeArtefatos"
          @abrir="abrirArtefatoDoIndice"
          @fechar="fecharArtefatoDoIndice"
        />
      </div>
    </transition>

    <div v-if="mostraIndiceDeMensagens" class="messages__indice">
      <ChatIndiceMensagens
        class="messages__indice-coluna"
        :mensagens="mensagensDoUsuario"
        :id-ativa="idMensagemAtiva"
        @rolar="rolarAteMensagem"
      />
    </div>

    <!-- Barra de carga da conversa (14/09/2026): é o ÚNICO indicador ao
         entrar numa conversa — o trilho fica vazio enquanto as mensagens
         chegam (sem spinner; um esqueleto existiu por algumas horas e saiu:
         a carga é rápida demais para ele fazer sentido). 4px em `primary`,
         a largura toda, no topo da ÁREA VISÍVEL deste container — mesma
         casca dos dois índices acima (`sticky; top: 0; height: 0`, a barra
         absoluta dentro dela), então não ocupa altura, não desloca o trilho
         nem o composer, e vale para toda superfície que monta este
         componente (rota cheia, imersiva, popup, ai-framework). Depois das
         cascas dos índices (o índice de artefatos é o PRIMEIRO filho por
         contrato — teste lê isso); com altura zero as três partem do mesmo
         ponto. `z-index` acima dos índices e do cartão de escopo flutuante
         da view. Entra e sai em fade (`iv2-carga`, `design/efeitos.sass`). -->
    <transition name="iv2-carga">
      <div v-if="carregandoMensagens" class="messages__carga">
        <v-progress-linear indeterminate absolute top height="4" color="primary" />
      </div>
    </transition>

    <!-- Trilho de leitura: no layout fundido a conversa é uma coluna
         centralizada de 768px, não a largura toda da tela.
         `iv2-carga-trilho` (`design/efeitos.sass`): a conversa ENTRA em fade
         + 6px de subida, na mesma duração em que a barra de carga some — sem
         `mode`, o nó é inserido no mesmo tick (`$refs.trilho` no `$nextTick`
         dos watchers de rolagem); a saída é sem animação (troca de conversa
         continua imediata). -->
    <transition name="iv2-carga-trilho">
      <div
        v-if="mostraTrilho"
        ref="trilho"
        :class="classeTrilho"
      >
        <!-- Cabeçalho da CONVERSA, dentro da rolagem e no trilho de leitura: quem
             entra aqui é lido antes da primeira mensagem e sai da tela junto com
             ela. É o que permite ao cartão de cliente em foco ficar preso ao
             início do fio em vez de pregado na viewport. Vazio na maioria das
             superfícies, e aí nada muda. -->
        <slot name="antes-das-mensagens" />

        <!-- `data-mensagem-id` cai no elemento raiz da bolha (atributo não-prop):
             é por ele que o índice rola até a mensagem. -->
        <ChatMessage
          v-for="linha in linhas"
          :key="linha.msg.id || linha.indice"
          :data-mensagem-id="linha.idDom"
          :id-conversa="idConversa"
          :store-module="storeModule"
          :atalhos="atalhos"
          :modulos="modulos"
          :message="linha.msg"
          :painel-de-progresso-ativo="painelProgressoContextoAtivo"
          :is-streaming="false"
          :persona="persona"
          :is-last="linha.isLast"
          :ancora-de-artefatos="linha.ancoraDeArtefatos"
          :respostas-do-formulario="linha.respostas"
          :comando-do-turno="linha.comando"
          :etapa-historica="ehEtapaHistorica(linha.msg)"
          :resposta-do-cartao="linha.respostaDoCartao"
          :estado-do-run="estadoDoRunDaConversa"
          :texto-revelado-inicial="textoReveladoDoTurno"
          @feedback="onFeedback"
          @open-service="onOpenService"
          @ask="onAsk"
          @respond="onRespond"
          @responder-perguntas="onResponderPerguntas"
          @revelado="onRevelado"
          @revelacao-concluida="onRevelacaoConcluida"
          @avancou-apresentacao="onAvancouApresentacao"
        />

        <!-- Mensagem em streaming. `@revelado`: o bastão da revelação suave
             (ver `onRevelado`) — a bolha persistida que nasce no `end` retoma
             o texto de onde esta parou.
             ★ merge PR #2242: as três props do Modo Agente (spec-002) seguem
             vivas nesta bolha — `toolCalls` (badges com `toolCallId`, Q-5),
             `planoStreaming` (US-10) e `aguardandoDecisaoDoRun` (US-13). -->
        <ChatMessage
          v-if="showStreamingMessage"
          :id-conversa="idConversa"
          :store-module="storeModule"
          :atalhos="atalhos"
          :modulos="modulos"
          :message="mensagemStreaming"
          :is-streaming="true"
          :active-tool-calls="toolCalls"
          :streaming-plano="planoStreaming"
          :painel-de-progresso-ativo="painelProgressoContextoAtivo"
          :streaming-busca="buscaStreaming"
          :streaming-codigo="codigoStreaming"
          :streaming-arquivo="arquivoStreaming"
          :comando-do-turno="comandoDoTurnoAoVivo"
          :aguardando-decisao="aguardandoDecisaoDoRun"
          :estado-do-run="estadoDoRunDaConversa"
          :streaming-tokens="responseTokens"
          :streaming-thinking-steps="thinkingSteps"
          :persona="persona"
          :streaming-servicos-citados="servicosCitadosStreaming"
          :streaming-perguntas-ao-gestor="perguntasAoGestorStreaming"
          :streaming-painel="painelStreaming"
          @open-service="onOpenService"
          @respond="onRespond"
          @revelado="onRevelado"
        />
      </div>
    </transition>

    <!-- ★ Conversa que NÃO pôde ser exibida (`errorCaptured`). Ocupa o lugar
         do trilho e do herói: em branco, o consultor acha que a conversa está
         só vazia e continua perguntando — e cada pergunta é cobrada sem
         aparecer. Fica dentro do trilho de leitura para nascer alinhado com as
         bolhas que deveriam estar aqui. -->
    <div v-if="erroNoTrilho" :class="classeTrilho" data-teste="messages-erro-no-trilho">
      <div class="messages__erro az-card rounded-sm pa-6">
        <div class="d-flex align-start">
          <icon-svg name="warning-status" :size="20" color="danger" class="mr-3 mt-1" />
          <div>
            <p class="ia-texto-trilho-erro-titulo mb-1">{{ TEXTO_ERRO.titulo }}</p>
            <p class="ia-texto-trilho-erro-corpo mb-0">{{ TEXTO_ERRO.corpo }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Carregando (troca em Recentes, palette): nada aqui — só a barra do
         topo; o slot vazio só entra quando NÃO há carga em curso, senão a
         tela caía no herói e depois carregava. -->
    <slot v-if="mostraVazio" name="empty" />

    <!-- Painel da conversa: registra as entregas conforme as partes chegam,
         sem cartão na bolha (§5.1). Fora do trilho para viver o turno inteiro. -->
    <MessagesEntregasAoVivo
      v-if="painelProgressoContextoAtivo"
      :store-module="storeModule"
    />

    <!-- Marca o fim do conteúdo real — onScroll()/scrollToBottom() medem
         contra ele em vez do scrollHeight do container (ver comentário em
         onScroll). Fica ANTES do spacer abaixo de propósito: a posição do
         sentinel não pode se mover junto com o espaço extra, senão a
         medição de "pinado no fim" passaria a mirar o fim do espaço vazio,
         não o fim do conteúdo real. -->
    <div ref="scrollSentinel" aria-hidden="true" />

    <!-- O respiro do ÚLTIMO turno: a folga de rolagem que deixa
         scrollNewUserMessageToTop() subir a pergunta nova ao topo — como
         CONTEÚDO comum dentro da área rolável (não padding do próprio
         `.messages`), não afeta o dimensionamento flex do container (ver
         histórico logo abaixo: um padding-bottom gigante no `.messages`
         quebrava isso de um jeito sério e não totalmente explicado).
         ★ [owner, 21/09/2026] A altura é MEDIDA (`medirEspacoDoTurno`), não
         60vh fixos, e não colapsa no `end`: era o colapso que jogava a tela
         "para baixo" quando a resposta ficava pronta. Zero fora de um turno
         iniciado nesta tela (conversa reaberta não tem respiro). -->
    <div
      ref="espacoDoTurno"
      class="messages__bottom-spacer"
      :style="{ height: alturaDoEspacoDoTurno + 'px' }"
      aria-hidden="true"
    />

    <!-- ★ [owner, 21/09/2026] "↓ Novas linhas" — só enquanto a resposta ainda
         chega E o leitor subiu (desgrudou do fim). Casca `sticky` no FUNDO da
         área visível sem ocupar altura (a mesma receita das cascas do topo),
         a pílula absoluta dentro dela. Clicar regruda e rola até o fim. -->
    <div class="messages__novas-linhas" aria-hidden="false">
      <transition name="iv2-carga">
        <button
          v-if="mostraNovasLinhas"
          type="button"
          class="messages__novas-linhas-pilula az-ds-selo d-inline-flex align-center"
          :aria-label="textoNovasLinhas.aria"
          data-teste="messages-novas-linhas"
          @click="voltarAoFimDaResposta"
        >
          <icon-svg name="arrow-down-new-ux" :size="12" color="currentColor" class="mr-1" />
          {{ textoNovasLinhas.rotulo }}
        </button>
      </transition>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '@modules/aawz-intelligence-v2/design/efeitos.sass'

// ★ O container que rola é o containing block de TODO descendente
// `position: absolute` (12/09/2026). Sem isto, um absoluto dentro de uma
// bolha — o `output.raciocinio-linha__anuncio` (live region só para leitor
// de tela) e o `.sr-only` do `agent-badge` — é posicionado contra o ancestral
// posicionado mais próximo, o `.intelligence-v2-chat__area`
// (`position: relative`), e por isso NÃO é recortado pelo `overflow` daqui:
// ele estende o `scrollHeight` da área (medido em `/home-ia`: 1687px numa
// área de 918px durante o streaming) e a área, mesmo `overflow: hidden`,
// passa a ser rolável por programação — qualquer `scrollIntoView`/`focus()`
// que precise de mais rolagem do que este container oferece a rola, e o
// composer sobe com ela. Com o container posicionado, os absolutos ficam
// dentro do conteúdo rolável, recortados, e a área nunca tem overflow.
// Sem `z-index`, então não cria contexto de empilhamento.
.messages
  position: relative

// ★ [owner, 21/09/2026] Casca da pílula "↓ Novas linhas": gruda no FUNDO da
// área visível sem ocupar altura — é o ÚLTIMO filho do container, então o
// `sticky; bottom` a segura na borda de baixo enquanto o conteúdo rola por
// trás. A pílula é absoluta dentro dela, centrada, e sobe 16px do fundo.
.messages__novas-linhas
  position: sticky
  bottom: 0
  height: 0
  z-index: 2

.messages__novas-linhas-pilula
  position: absolute
  bottom: 16px
  left: 50%
  transform: translateX(-50%)
  padding: 6px 12px
  border: 1px solid var(--neutral-200)
  border-radius: 999px
  background: var(--white)
  color: var(--neutral-700)
  box-shadow: var(--shadow-sm)
  cursor: pointer
  white-space: nowrap
  transition: background var(--motion-duration-fast), color var(--motion-duration-fast)

  &:hover,
  &:focus-visible
    background: var(--neutral-100)
    color: var(--neutral-900)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// Casca da barra de carga: gruda no TOPO da área visível sem ocupar altura
// (a barra é `absolute top` dentro dela). Acima do índice de artefatos (2)
// e do cartão de escopo flutuante da view (2).
.messages__carga
  position: sticky
  top: 0
  height: 0
  z-index: 3

// Casca do índice de artefatos: gruda no TOPO da área visível sem ocupar
// altura; o índice é absoluto dentro dela, no canto superior direito
// (`top: 12px; right: 16px`), por cima do índice de mensagens (`z-index`).
// A largura da casca é a do container, não a do trilho — por isso o cartão
// recebe `largura-maxima` medida (ver o template), senão invade o trilho
// quando a lateral da imersiva está aberta.
.messages__indice-artefatos
  position: sticky
  top: 0
  height: 0
  z-index: 2

.messages__indice-artefatos-chip
  position: absolute
  top: 12px
  right: 16px

// Casca do índice: gruda a 70% da área visível do container que rola (terço
// de baixo — o canto de cima é do cartão do painel da conversa, que assim não
// cobre o índice) e não ocupa altura — quem tem tamanho é a coluna absoluta,
// centrada nela pelo `translateY(-50%)`. `z-index` para a coluna ficar por cima
// das bolhas (`.chat-message` cria contexto de empilhamento próprio em alguns
// estados). O popover abre à esquerda e o `v-menu` o mantém dentro da janela.
//
// ⚠️ O número já andou três vezes, e nas três quem mediu foi o olho do owner:
// 50% até 17/09/2026 (o cartão do painel tomou o canto de cima e um cobria o
// outro), 70% desde então, 60% por algumas horas em 20/09/2026 (*"sobe um
// pouco…"*) e **de volta a 70% no mesmo dia** (*"volte para a posição original
// dele"*). Não mexa sem a tela na frente.
$posicao-do-indice-de-mensagens: 70%

.messages__indice
  position: sticky
  top: $posicao-do-indice-de-mensagens
  height: 0
  z-index: 1

.messages__indice-coluna
  position: absolute
  right: 12px
  top: 0
  transform: translateY(-50%)

// Trilho de leitura. A LARGURA mora na partial `_ia-chat-trilho` (a mesma
// medida governa o composer, em outro arquivo); aqui fica só o ritmo interno.
.messages__trilho
  display: flex
  flex-direction: column
  // `gap-8` + `py-8` do protótipo (`chat.tsx`). As mensagens nascem com `mb-4`
  // porque no popup e na tela hospedeira o espaçamento continua sendo delas;
  // dentro do trilho quem manda é o `gap`, senão soma 16 + 32 = 48px.
  // `!important` é obrigatório: `mb-4` é utilitária do Vuetify
  // (`.v-application .mb-4 { margin-bottom: 16px !important }`).
  gap: 32px
  // 16px na lateral, não os 20px (`px-5`) do protótipo: é o mesmo respiro do
  // `px-4` que o composer já traz por dentro, então as duas caixas nascem no
  // MESMO eixo vertical sem precisar brigar com uma utilitária `!important`
  // do Vuetify. 4px de diferença contra o protótipo, alinhamento de graça.
  padding: 32px 16px

  > .chat-message
    margin-bottom: 0 !important

// Histórico: um `padding-bottom: 100vh !important` tentava garantir espaço
// pra scrollNewUserMessageToTop() sempre conseguir subir a última mensagem
// pro topo. Removido — testado ao vivo e causava um bug sério: `.messages`
// é item flex (`flex-grow-1`, pai em coluna) e, assim que o padding gigante
// passou a valer de verdade (tinha um empate de especificidade com
// `.v-application .pa-4` do Vuetify que mascarava o problema até então),
// a caixa inflava pro tamanho do conteúdo+padding em vez de ficar contida
// no espaço do pai — nem `min-height:0` nem `height:0 !important` via JS
// conseguiram corrigir isso, empurrando o composer inteiro pra fora da área
// visível do popup. Substituído por `.messages__bottom-spacer`: um filho
// comum dentro da área rolável (não padding do container), que não
// participa do cálculo de tamanho flex do `.messages` — só cresce o
// scrollHeight interno, sem inflar a caixa em si.
.messages__bottom-spacer
  height: 0
  flex-shrink: 0
  // Sem transition de propósito: a altura é remedida a cada crescimento do
  // trilho (`medirEspacoDoTurno`), e a soma conteúdo + respiro fica parada —
  // animar a altura deixaria o conteúdo "escorregando" por baixo.

// Conversa que não pôde ser exibida (`errorCaptured`). Tokens, nunca hex: o
// título herda o cinza mais forte do texto e o corpo o de leitura. O cartão é
// o `az-card` do DS (classe no template), aqui só a medida e a tipografia.
.messages__erro
  margin-top: 24px
</style>
